/* THE ENTITY LIST, edited where people are placed in it — Jay, 2026-10-08 ("HINC HUSA HEMA HEVN HPAC
   HEMY HAUS / MS / TS . etc"). The "etc" is the point: the list will grow, and a new office or vendor
   should be a row an Admin adds here rather than a migration somebody has to be asked for.

   The CODE is fixed once added — it is stored on every person in the group, so changing it would be a
   migration, not an edit. The name, the kind and whether it is in use can change. An entity is
   SWITCHED OFF, never deleted: people may still carry it, and the row is what their group is named by.
   Each row saves on its own, because each is one fact about one entity; one Save for the whole table
   would make a typo in one name hold back switching another off. */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';
import { BACKEND_URL } from './backendConfig';
import useEscapeClose from './useEscapeClose';
import { useTranslation } from 'react-i18next';

// The stored kind and the key of its label; the label is read where it is drawn.
// partner (Jay 02/11): a partner company such as DAC VN, kept apart from the Helinox companies
const KINDS = [['helinox', 'xUsers.ent.kindHelinox'], ['partner', 'xUsers.ent.kindPartner'], ['vendor', 'xUsers.ent.kindVendor']];
const API = `${BACKEND_URL}/api/admin/entities`;

function Row({ en, onSaved }) {
    const { t } = useTranslation();
    const [name, setName] = useState(en.name);
    const [kind, setKind] = useState(en.kind);
    const [active, setActive] = useState(!!en.isActive);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    useEffect(() => { setName(en.name); setKind(en.kind); setActive(!!en.isActive); }, [en]);
    const dirty = name.trim() !== en.name || kind !== en.kind || active !== !!en.isActive;
    const save = async () => {
        setBusy(true); setMsg(null);
        try {
            await axios.put(`${API}/${encodeURIComponent(en.key)}`, { name: name.trim(), kind, isActive: active }, { withCredentials: true });
            setMsg({ ok: true, text: t('xUsers.ent.saved') });
            onSaved();
        } catch (e) {
            setMsg({ ok: false, text: e.response?.data?.error || t('xUsers.ent.saveFailed') });
        } finally { setBusy(false); }
    };
    return (
        <tr className={active ? '' : 'ue-off'}>
            <td className="ue-code">{en.key}</td>
            <td><input value={name} onChange={(e) => setName(e.target.value)} aria-label={t('xUsers.ent.nameOf', { code: en.key })} /></td>
            <td>
                <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label={t('xUsers.ent.kindOf', { code: en.key })}>
                    {KINDS.map(([k, l]) => <option key={k} value={k}>{t(l)}</option>)}
                </select>
            </td>
            <td className="ue-num">{en.users}</td>
            <td><label className="ue-check"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> {t('xUsers.ent.inUse')}</label></td>
            <td className="ue-act">
                <button type="button" className="lu-btn primary small" disabled={!dirty || busy || !name.trim()} onClick={save}>{busy ? t('xUsers.ent.saving') : t('xUsers.ent.save')}</button>
                {msg && <span className={`ue-msg ${msg.ok ? 'ok' : 'err'}`}>{msg.text}</span>}
            </td>
        </tr>
    );
}

export default function EntityManager({ entities, onClose, onChanged }) {
    const { t } = useTranslation();
    const [add, setAdd] = useState({ key: '', name: '', kind: 'helinox' });
    const [addMsg, setAddMsg] = useState(null);
    const [adding, setAdding] = useState(false);
    useEscapeClose(onClose, !adding);

    const create = async () => {
        setAdding(true); setAddMsg(null);
        try {
            await axios.post(API, { key: add.key.trim(), name: add.name.trim(), kind: add.kind }, { withCredentials: true });
            setAddMsg({ ok: true, text: t('xUsers.ent.added', { code: add.key.trim() }) });
            setAdd({ key: '', name: '', kind: add.kind });
            onChanged();
        } catch (e) {
            setAddMsg({ ok: false, text: e.response?.data?.error || t('xUsers.ent.addFailed') });
        } finally { setAdding(false); }
    };

    return (
        <div className="eu-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="eu-modal ue-modal" role="dialog" aria-label={t('xUsers.ent.title')}>
                <div className="eu-modal-head">
                    <h3>{t('xUsers.ent.title')}</h3>
                    <button type="button" onClick={onClose} title={t('xUsers.ent.close')} aria-label={t('xUsers.ent.close')}><X size={18} /></button>
                </div>
                <div className="eu-modal-body">
                    <p className="ue-intro">{t('xUsers.ent.intro')}</p>
                    <table className="ue-table">
                        <thead>
                            <tr><th>{t('xUsers.ent.colCode')}</th><th>{t('xUsers.ent.colName')}</th><th>{t('xUsers.ent.colKind')}</th><th className="ue-num" title={t('xUsers.ent.colPeopleTitle')}>{t('xUsers.ent.colPeople')}</th><th>{t('xUsers.ent.colInUse')}</th><th /></tr>
                        </thead>
                        <tbody>
                            {entities.map((en) => <Row key={en.key} en={en} onSaved={onChanged} />)}
                        </tbody>
                    </table>
                    <div className="ue-add">
                        <span className="ue-add-title">{t('xUsers.ent.addTitle')}</span>
                        <div className="ue-add-row">
                            <input placeholder={t('xUsers.ent.codePh')} value={add.key} maxLength={20} onChange={(e) => setAdd({ ...add, key: e.target.value })} aria-label={t('xUsers.ent.newCode')} />
                            <input placeholder={t('xUsers.ent.namePh')} value={add.name} maxLength={200} onChange={(e) => setAdd({ ...add, name: e.target.value })} aria-label={t('xUsers.ent.newName')} />
                            <select value={add.kind} onChange={(e) => setAdd({ ...add, kind: e.target.value })} aria-label={t('xUsers.ent.newKind')}>
                                {KINDS.map(([k, l]) => <option key={k} value={k}>{t(l)}</option>)}
                            </select>
                            <button type="button" className="lu-btn primary small" disabled={adding || !add.key.trim() || !add.name.trim()} onClick={create}>{adding ? t('xUsers.ent.adding') : t('xUsers.ent.add')}</button>
                        </div>
                        {addMsg && <div className={`ue-msg ${addMsg.ok ? 'ok' : 'err'}`}>{addMsg.text}</div>}
                    </div>
                </div>
                <div className="eu-modal-foot">
                    <button type="button" className="lu-btn ghost" onClick={onClose}>{t('xUsers.ent.close')}</button>
                </div>
            </div>
            <style jsx="true">{`
                .ue-modal { max-width: 860px; }
                .ue-intro { margin: 0 0 14px; font-size: 13px; color: #475569; line-height: 1.5; }
                .ue-table { width: 100%; border-collapse: separate; border-spacing: 0; }
                .ue-table th { text-align: left; font-size: 12px; font-weight: 800; color: #334155; background: #F1F5F9; padding: 8px 8px; }
                .ue-table td { padding: 6px 8px; border-bottom: 1px solid #EEF2F7; font-size: 13.5px; vertical-align: middle; }
                .ue-table input:not([type="checkbox"]), .ue-table select, .ue-add input, .ue-add select {
                    border: 1px solid #D8E0EC; border-radius: 7px; padding: 6px 9px; font-size: 13.5px; color: #111827; background: #fff; width: 100%; box-sizing: border-box;
                }
                .ue-code { font-family: ui-monospace, monospace; font-weight: 800; color: #1F3864; white-space: nowrap; }
                .ue-num { text-align: right; width: 60px; }
                .ue-check { display: inline-flex !important; flex-direction: row !important; align-items: center; gap: 6px; font-size: 13px !important; white-space: nowrap; }
                .ue-act { white-space: nowrap; width: 150px; }
                .ue-off td { color: #9CA3AF; }
                .ue-off .ue-code { color: #9CA3AF; }
                .ue-msg { margin-left: 8px; font-size: 12.5px; font-weight: 700; }
                .ue-msg.ok { color: #15803D; }
                .ue-msg.err { color: #B42318; }
                div.ue-msg { margin: 8px 0 0; }
                .ue-add { margin-top: 16px; border: 1px solid #E5EAF2; border-radius: 10px; padding: 12px; background: #F9FBFD; }
                .ue-add-title { display: block; font-size: 11px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; color: #6B7280; margin-bottom: 8px; }
                .ue-add-row { display: grid; grid-template-columns: 150px 1fr 130px auto; gap: 8px; align-items: center; }
                @media (max-width: 640px) { .ue-add-row { grid-template-columns: 1fr; } }
            `}</style>
        </div>
    );
}
